<script lang="ts">
import { defineComponent, inject, PropType, type Ref, ref, computed } from 'vue';
import { ProductItem, ProductVariation } from 'src/core/types/Products';
import { getLowestPriceFromVariations, getVariationImages } from 'src/core/utils/Products';
import { FavouriteStore } from 'stores/Favourite/Favourite.ts';
import { useQuasar } from 'quasar';
import { useRouter } from 'vue-router';

export default defineComponent({
  name: 'ProductCard',

  props: {
    item: {
      type: Object as PropType<ProductItem>,
      required: true,
    },
  },

  setup(props) {
    const $q = useQuasar();
    const $router = useRouter();
    const variations = props.item.variations;
    const variationImages = getVariationImages(variations);
    const isLogged = inject<Ref<boolean>>('isLogged');
      const siteAddress = inject<string>('siteAddress');

    const productImages = ref<Record<string, Record<string, string>>>({});
    const sliceValue = Object.keys(variationImages).length === 1 ? 3 : 1;
    variations.forEach((variation) => {
      if (variationImages[variation.code]) {
        Object.keys(variationImages[variation.code])
          .slice(0, sliceValue)
          .forEach((imgUrl) => {
            productImages.value[imgUrl] = variationImages[variation.code][imgUrl] as Record<string, string>;
          });
      }
    });

    const productPrice = props.item.type === 0 ? variations[0].price : getLowestPriceFromVariations(variations);

    const isDiscount = ref(false);
    isDiscount.value = !variations.every((variation: ProductVariation) => variation.discount === 0);

    const outOfStock = computed(() => {
      return variations.every((variation: ProductVariation) => Number(variation.stock) === 0);
    });

    const productImageKeys = computed(() => Object.keys(productImages.value));
    const activeImageIndex = ref(productImageKeys.value.length > 0 ? productImageKeys.value[0] : null);

    const favouriteStore = FavouriteStore();
    const favouriteLoading = ref(false);

    const addToFavourites = (productCode: number) => {
      if (!isLogged?.value) {
        $router.push('/login-or-register');
        return;
      }
      favouriteLoading.value = true;
      favouriteStore
        .addToFavourites(productCode)
        .then(({ status, message }) => {
          if (status._200_) {
            $q.notify({
              color: 'green',
              icon: 'fa-regular fa-circle-check',
              message: 'Ürün favori listenize eklenmiştir',
              badgeColor: 'white',
              badgeTextColor: 'green-10',
            });
          } else {
            $q.notify({
              color: 'red',
              icon: 'fa-solid fa-circle-exclamation',
              message,
            });
          }
        })
        .finally(() => {
          favouriteLoading.value = false;
        });
    };

    return {
      productImages,
      productImageKeys,
      productPrice,
      activeImageIndex,
      favouriteLoading,
      isDiscount,
      outOfStock,
      addToFavourites,
      siteAddress,
    };
  },

  methods: {
    isActiveImage(index: string) {
      return this.activeImageIndex === index;
    },
  },
});
</script>

<template>
  <div class="product-card-modern">
    <!-- Image Section -->
    <div class="card-image-container">
      <div v-if="outOfStock" class="card-badge out-of-stock-badge">STOKTA YOK</div>
      <div v-else-if="isDiscount" class="card-badge discount-badge">İNDİRİMDE</div>

      <a :href="`${siteAddress}${item.link.startsWith('/') ? '' : '/'}${item.link}`" class="image-link" :class="{ 'out-of-stock-img': outOfStock }">
        <transition name="fade-scale" mode="out-in">
          <img
            :key="activeImageIndex"
            :src="productImages[activeImageIndex]?.detail"
            :srcset="`
              ${productImages[activeImageIndex]?.small} 150w,
              ${productImages[activeImageIndex]?.middle} 320w,
              ${productImages[activeImageIndex]?.detail} 462w
            `"
            sizes="(max-width: 768px) 100vw, 320px"
            :alt="item.name"
            class="product-img"
            loading="lazy"
          />
        </transition>

        <!-- Image Navigation Layers (Invisible hover zones) -->
        <div class="image-hover-layers">
          <div v-for="key in productImageKeys" :key="key" class="hover-zone" @mouseenter="activeImageIndex = key"></div>
        </div>
      </a>

      <!-- Quick Actions Overlay -->
      <div class="card-actions-overlay">
        <q-btn flat round dense @click.stop="addToFavourites(item.code)" :loading="favouriteLoading" class="action-btn fav-btn">
          <q-icon name="fa-regular fa-heart" size="1.2rem" />
          <q-tooltip anchor="top middle" self="bottom middle">Favorilere Ekle</q-tooltip>
        </q-btn>

        <q-btn flat round dense :href="`/${item.link}`" class="action-btn view-btn">
          <q-icon name="visibility" size="1.2rem" />
          <q-tooltip anchor="top middle" self="bottom middle">İncele</q-tooltip>
        </q-btn>
      </div>

      <!-- Image Progress Dots -->
      <div v-if="productImageKeys.length > 1" class="image-dots-container">
        <div v-for="key in productImageKeys" :key="key" class="dot" :class="{ active: activeImageIndex === key }"></div>
      </div>
    </div>

    <!-- Product Info Section -->
    <div class="card-info">
      <div class="product-name-wrapper">
        <a :href="`/${item.link}`" class="product-name">
          {{ item.name }}
        </a>
      </div>

      <div class="product-footer">
        <div class="price-container">
          <span class="price-value">{{ productPrice }}</span>
          <span class="currency">₺</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.product-card-modern {
  --card-radius: 16px;
  --transition-speed: 0.3s;
  --primary-font: 'Urbanist', sans-serif;
  --secondary-font: 'EB Garamond', serif;
  --accent-color: #ff3b30;
  --card-background: #ffffff;

  position: relative;
  background: var(--card-background);
  border-radius: var(--card-radius);
  overflow: hidden;
  transition: all var(--transition-speed) cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  max-width: 320px;
  margin: 0 auto;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.product-card-modern:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
  border-color: transparent;
}

/* Image Container */
.card-image-container {
  position: relative;
  aspect-ratio: 4 / 5;
  overflow: hidden;
  background: #f8f8f8;
}

.image-link {
  display: block;
  width: 100%;
  height: 100%;
}

.product-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.product-card-modern:hover .product-img {
  transform: scale(1.08);
}

/* Hover Layers */
.image-hover-layers {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  z-index: 2;
}

.hover-zone {
  flex: 1;
  height: 100%;
}

/* Badge */
.card-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 3;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.discount-badge {
  background: var(--accent-color);
  color: white;
  box-shadow: 0 4px 10px rgba(255, 59, 48, 0.3);
}

.out-of-stock-badge {
  background: #1d1d1f;
  color: white;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
}

.out-of-stock-img {
  opacity: 0.85;
  filter: grayscale(85%);
}

/* Actions Overlay */
.card-actions-overlay {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%) translateX(20px);
  display: flex;
  flex-direction: column;
  gap: 10px;
  z-index: 4;
  opacity: 0;
  transition: all 0.3s ease;
}

.product-card-modern:hover .card-actions-overlay {
  opacity: 1;
  transform: translateY(-50%) translateX(0);
}

.action-btn {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(8px);
  color: #1d1d1f;
  transition: all 0.2s ease;
  width: 40px;
  height: 40px;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.action-btn:hover {
  background: #1d1d1f;
  color: white;
  transform: scale(1.1);
}

.fav-btn:active {
  transform: scale(0.9);
}

/* Image Dots */
.image-dots-container {
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 6px;
  z-index: 3;
}

.dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
}

.dot.active {
  width: 18px;
  border-radius: 10px;
  background: #1d1d1f;
}

/* Info Section */
.card-info {
  padding: 16px;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 8px;
}

.product-name-wrapper {
  flex-grow: 1;
}

.product-name {
  font-family: var(--secondary-font);
  font-size: 1.15rem;
  font-weight: 500;
  color: #1d1d1f;
  text-decoration: none;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  transition: color 0.2s ease;
}

.product-name:hover {
  color: var(--accent-color);
}

.product-footer {
  margin-top: auto;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.price-container {
  font-family: var(--primary-font);
  display: flex;
  align-items: baseline;
  gap: 2px;
}

.currency {
  font-size: 1rem;
  font-weight: 600;
  color: #1d1d1f;
  margin-left: 0.3rem;
}

.price-value {
  font-size: 1.4rem;
  font-weight: 700;
  color: #1d1d1f;
  letter-spacing: -0.5px;
}

/* Animations */
.fade-scale-enter-active,
.fade-scale-leave-active {
  transition: all 0.4s ease;
}

.fade-scale-enter-from {
  opacity: 0;
  transform: scale(1.05);
}

.fade-scale-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .product-card-modern {
    max-width: 100%;
  }
  .card-actions-overlay {
    opacity: 1;
    transform: translateY(-50%) translateX(0);
    right: 8px;
  }
  .action-btn {
    width: 36px;
    height: 36px;
  }
}
</style>
